import {
  CircleNotchIcon,
  UploadSimpleIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { FileTrigger } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { FieldErrorText, Label, labelVariants } from "@/components/ui/field";
import IconButton from "@/components/ui/icon-button";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { JollyTextField } from "@/components/ui/textfield";
import { useFileDropZone } from "@/hooks/useFileDropZone";
import { cn } from "@/lib/utils";

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Waits for typing to pause before loading the URL, so partial URLs don't show errors. */
const URL_LOAD_DELAY_MS = 300;

type ReplaceSource = "upload" | "url";

export interface ImageReplaceFieldProps {
  label: string;
  /** Browser URL of the image the briefing uses now. */
  src: string;
  alt: string;
  /** Resolves `null` once the chosen file is applied, which closes the panel, or an error message to show. */
  onFileApply: (file: File) => Promise<string | null>;
  /** Resolves `null` once the image at `url` is applied, which closes the panel, or an error message to show. */
  onUrlApply: (url: string) => Promise<string | null>;
  /** Pass only while the briefing uses a replacement; shows Reset to template image. */
  onReset?: () => void;
  /** Shows the current image with Replace image and Reset to template image disabled. */
  isDisabled?: boolean;
}

/** A briefing's current image with a panel to replace it. */
export const ImageReplaceField = ({
  label,
  src,
  alt,
  onFileApply,
  onUrlApply,
  onReset,
  isDisabled = false,
}: ImageReplaceFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState<ReplaceSource>("upload");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState("");
  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [urlToLoad, setUrlToLoad] = useState("");
  const [loadedUrl, setLoadedUrl] = useState("");
  const [failedUrl, setFailedUrl] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setFilePreviewUrl(previewUrl);
    return () => {
      URL.revokeObjectURL(previewUrl);
      setFilePreviewUrl("");
    };
  }, [file]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setUrlToLoad(url),
      URL_LOAD_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [url]);

  const close = () => {
    setIsOpen(false);
    setSource("upload");
    setUrl("");
    setUrlToLoad("");
    setLoadedUrl("");
    setFailedUrl("");
    setFile(null);
    setIsFileLoaded(false);
    setError("");
  };

  const chooseFile = (chosenFile: File | undefined) => {
    if (!chosenFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(chosenFile.type)) {
      setError("Use a PNG, JPG or WebP image.");
    } else if (chosenFile.size > MAX_IMAGE_BYTES) {
      setError("Use an image up to 10 MB.");
    } else {
      setError("");
      setIsFileLoaded(false);
      setFile(chosenFile);
    }
  };

  const { isDragOver, dropZoneProps } = useFileDropZone((files) =>
    chooseFile(files[0]),
  );

  const apply = async (applyImage: () => Promise<string | null>) => {
    setError("");
    setIsApplying(true);
    const applyError = await applyImage().catch(
      () => "The image couldn't be applied. Try again.",
    );
    setIsApplying(false);
    if (applyError === null) close();
    else setError(applyError);
  };

  const actions = (
    onApply: () => void,
    canApply: boolean,
    isChecking = false,
  ) => (
    <div className="grid grid-cols-2 gap-3">
      <Button variant="outline" onPress={close} isDisabled={isApplying}>
        Cancel
      </Button>
      <Button
        className="gap-2"
        onPress={onApply}
        isDisabled={isApplying || !canApply}
      >
        {isApplying || isChecking ? (
          <CircleNotchIcon size={16} className="animate-spin" aria-hidden />
        ) : null}
        Apply image
      </Button>
    </div>
  );

  // Label styles on a span: these headings have no control for a <label> to point at.
  const headingClassName = cn(labelVariants(), "mb-0 text-gray-700");
  const imageClassName =
    "h-21.75 w-full rounded-sm border border-neutral-200 object-cover shadow-sm";

  return (
    <div className="flex flex-col gap-2">
      <span className={headingClassName}>{label}</span>
      {file ? null : (
        <>
          <p className="text-sm text-neutral-700">
            Use the existing image, or upload a replacement for this briefing.
          </p>
          <img src={src} alt={alt} className={imageClassName} />
        </>
      )}
      {isOpen ? (
        <div className="flex flex-col gap-4 rounded-sm border border-neutral-200 p-4">
          {file ? (
            <>
              <span className={headingClassName}>New image</span>
              <div className="relative">
                {filePreviewUrl ? (
                  <img
                    src={filePreviewUrl}
                    alt={file.name}
                    className={imageClassName}
                    onLoad={() => setIsFileLoaded(true)}
                    onError={() => {
                      setFile(null);
                      setError("This file can't be read as an image.");
                    }}
                  />
                ) : null}
                <IconButton
                  icon={<XIcon />}
                  aria-label="Cancel new image"
                  variant="icon-only"
                  size="sm"
                  className="absolute -top-3 -right-3 rounded-full bg-gray-50 shadow-sm"
                  onPress={close}
                  isDisabled={isApplying}
                />
                <FieldErrorText>{error}</FieldErrorText>
              </div>
              {actions(() => apply(() => onFileApply(file)), isFileLoaded)}
            </>
          ) : (
            <>
              <RadioGroup
                value={source}
                onChange={(value) => {
                  setSource(value as ReplaceSource);
                  setError("");
                }}
                isDisabled={isApplying}
                className="gap-3"
              >
                <Label className="mb-0 text-gray-700">Replace image</Label>
                <Radio value="upload">Upload an image</Radio>
                <Radio value="url">Use an image URL</Radio>
              </RadioGroup>
              {source === "upload" ? (
                <>
                  <div>
                    <fieldset
                      aria-label="Choose a new image"
                      className={cn(
                        "flex flex-col items-center gap-1 rounded-sm border border-dashed border-neutral-300 px-4 py-6",
                        isDragOver && "border-violet-400 bg-violet-50/60",
                      )}
                      {...dropZoneProps}
                    >
                      <UploadSimpleIcon size={16} aria-hidden />
                      <span className="mt-1 text-sm font-medium text-gray-700">
                        Choose a new image
                      </span>
                      <span className="text-[11px] text-neutral-600">
                        PNG, JPG or WebP
                      </span>
                      <FileTrigger
                        acceptedFileTypes={ACCEPTED_IMAGE_TYPES}
                        onSelect={(files) => chooseFile(files?.[0])}
                      >
                        <Button className="mt-2 w-48">Browse files</Button>
                      </FileTrigger>
                    </fieldset>
                    <FieldErrorText>{error}</FieldErrorText>
                  </div>
                  <Button variant="outline" onPress={close}>
                    Cancel
                  </Button>
                </>
              ) : (
                <div className="flex flex-col gap-3 pl-8.5">
                  <JollyTextField
                    aria-label="Image URL"
                    placeholder="https://"
                    type="url"
                    value={url}
                    onChange={(value) => {
                      setUrl(value);
                      setError("");
                    }}
                    isDisabled={isApplying}
                    errorMessage={
                      error ||
                      (url !== "" && url === failedUrl
                        ? "This URL doesn't load an image."
                        : "")
                    }
                    clearable={false}
                  />
                  {urlToLoad ? (
                    <img
                      // Remounts per URL, so a late event from the last URL can't count for this one.
                      key={urlToLoad}
                      src={urlToLoad}
                      alt=""
                      hidden
                      // Some hosts refuse hotlinked images that send a referrer.
                      referrerPolicy="no-referrer"
                      onLoad={() => setLoadedUrl(urlToLoad)}
                      onError={() => setFailedUrl(urlToLoad)}
                    />
                  ) : null}
                  {actions(
                    () => apply(() => onUrlApply(url)),
                    url !== "" && loadedUrl === url,
                    url !== "" &&
                      urlToLoad === url &&
                      loadedUrl !== url &&
                      failedUrl !== url,
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex gap-3">
          {onReset ? (
            <Button
              variant="ghost"
              className="flex-1"
              onPress={onReset}
              isDisabled={isDisabled}
            >
              Reset to template image
            </Button>
          ) : null}
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onPress={() => setIsOpen(true)}
            isDisabled={isDisabled}
          >
            <UploadSimpleIcon size={16} aria-hidden />
            Replace image
          </Button>
        </div>
      )}
    </div>
  );
};
